import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Avatar,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Slider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  PhotoCamera as PhotoCameraIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
// removed Autocomplete — simple service form does not collect providers
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  getDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage, auth } from "../firebase";

const normalizeIncludedItems = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const formatIncludedItemsForInput = (service) =>
  normalizeIncludedItems(
    service?.whatsIncluded ||
      service?.whatIncluded ||
      service?.included ||
      service?.includes ||
      service?.features
  ).join("\n");

const getImagePosition = (x = 50, y = 50) => `${x}% ${y}%`;

// Per-service distance restriction. Stored on each services/{id} doc as:
//   distanceLogicEnabled: boolean  (ON = apply radius, OFF = no distance restriction)
//   distanceLimitKm: number        (radius used when ON)
// Services saved before this setting existed have no field; treat them as ON with
// the default radius so the app's current behavior is unchanged until an admin edits them.
const DEFAULT_DISTANCE_LIMIT_KM = 20;
const DISTANCE_LOG_PREFIX = "[ServiceDistanceLogic]";

const isDistanceLogicEnabled = (service) =>
  service?.distanceLogicEnabled !== false;

const getDistanceLimitKm = (service) => {
  const km = Number(service?.distanceLimitKm);
  return Number.isFinite(km) && km > 0 ? km : DEFAULT_DISTANCE_LIMIT_KM;
};

const Services = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("list"); // default to 'list' view per request
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    category: "",
    subcategory: "",
    whatsIncluded: "",
    imageFit: "cover",
    imagePositionX: 50,
    imagePositionY: 50,
    status: "active",
    discount: 0,
    commissionRate: 20,
    distanceLogicEnabled: true,
    distanceLimitKm: DEFAULT_DISTANCE_LIMIT_KM,
  });
  const [distanceTogglingId, setDistanceTogglingId] = useState(null);

  const fetchPlatformSettings = async () => {
    try {
      const docRef = doc(db, "platformSettings", "main");
      const docSnap = await getDoc(docRef);
      setPlatformSettings(
        docSnap.exists()
          ? docSnap.data()
          : {
              defaultCommissionRate: 20,
              commissionType: "percentage",
              platformFee: 5,
            }
      );
    } catch (err) {
      console.error("Error fetching platform settings:", err);
      // Set default settings even on error
      setPlatformSettings({
        defaultCommissionRate: 20,
        commissionType: "percentage",
        platformFee: 5,
      });
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError("");
      const q = query(collection(db, "services"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const servicesData = [];
      querySnapshot.forEach((doc) => {
        servicesData.push({ id: doc.id, ...doc.data() });
      });
      setServices(servicesData);
      console.log(
        `${DISTANCE_LOG_PREFIX} Loaded ${servicesData.length} services (missing field = ON by default)`
      );
      console.table(
        servicesData.map((s) => ({
          id: s.id,
          title: s.title || s.name,
          storedEnabled: s.distanceLogicEnabled,
          effective: isDistanceLogicEnabled(s) ? "ON" : "OFF",
          limitKm: getDistanceLimitKm(s),
        }))
      );
    } catch (err) {
      console.error("Error fetching services:", err);
      setError("Failed to fetch services. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformSettings();
    fetchServices();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const q = query(
        collection(db, "categories"),
        orderBy("createdAt", "desc")
      );
      const qs = await getDocs(q);
      const cats = [];
      qs.forEach((d) => cats.push({ id: d.id, ...d.data() }));
      setCategories(cats);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    try {
      const col = collection(db, "categories", categoryId, "subcategories");
      const qs = await getDocs(query(col, orderBy("createdAt", "desc")));
      const subs = [];
      qs.forEach((d) => subs.push({ id: d.id, ...d.data() }));
      setSubcategories(subs);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setSubcategories([]);
    }
  };

  // Keep selectedProvider in sync when dialog opens or providers list updates
  useEffect(() => {
    // no-op: dialog open effects handled elsewhere for simplified form
  }, [dialogOpen]);

  // Cleanup blob URLs when imagePreview changes or component unmounts
  useEffect(() => {
    return () => {
      if (
        imagePreview &&
        imagePreview.startsWith &&
        imagePreview.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(imagePreview);
        } catch (e) {
          /* ignore */
        }
      }
    };
  }, [imagePreview]);

  // Fetch users and filter providers (clients/providers)
  // providers removed — service creation simplified and does not assign providers

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImageFile(file);
      try {
        const url = URL.createObjectURL(file);
        setImagePreview(url);
      } catch (err) {
        setImagePreview(null);
      }
    }
  };
  const handleAddNew = () => {
    setEditingService(null);
    setFormData({
      title: "",
      description: "",
      price: "",
      duration: "",
      category: "",
      subcategory: "",
      whatsIncluded: "",
      imageFit: "cover",
      imagePositionX: 50,
      imagePositionY: 50,
      status: "active",
      discount: 0,
      commissionRate: platformSettings?.defaultCommissionRate || 20,
      distanceLogicEnabled: true,
      distanceLimitKm: DEFAULT_DISTANCE_LIMIT_KM,
    });
    setSelectedImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || service.name || "",
      description: service.description || "",
      price: service.price || "",
      duration: service.duration || "",
      category: service.category || "",
      subcategory: service.subcategory || service.subCategory || "",
      whatsIncluded: formatIncludedItemsForInput(service),
      imageFit: service.imageFit || "cover",
      imagePositionX: service.imagePositionX ?? 50,
      imagePositionY: service.imagePositionY ?? 50,
      status: service.status || "active",
      discount: service.discount || 0,
      commissionRate:
        service.commissionRate || platformSettings?.defaultCommissionRate || 20,
      distanceLogicEnabled: isDistanceLogicEnabled(service),
      distanceLimitKm: getDistanceLimitKm(service),
    });
    console.log(`${DISTANCE_LOG_PREFIX} Editing service`, {
      serviceId: service.id,
      title: service.title || service.name,
      storedDistanceLogicEnabled: service.distanceLogicEnabled,
      storedDistanceLimitKm: service.distanceLimitKm,
      resolvedEnabled: isDistanceLogicEnabled(service),
      resolvedLimitKm: getDistanceLimitKm(service),
    });
    setSelectedImageFile(null);
    setImagePreview(service.imageUrl || null);
    // provider assignment removed in simplified form
    // If service has a category, pre-load its subcategories so the select shows options
    if (service.category) {
      const cat = categories.find((c) => c.name === service.category);
      if (cat) {
        fetchSubcategories(cat.id);
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.price) {
      showSnackbar("Please fill in required fields (title and price)", "error");
      return;
    }
    // no provider required in simplified form
    // Basic numeric validation for price only
    const priceVal = parseFloat(formData.price);
    if (isNaN(priceVal) || priceVal <= 0) {
      showSnackbar(
        "Please enter a valid service price greater than 0",
        "error"
      );
      return;
    }
    const distanceLimitVal = Number(formData.distanceLimitKm);
    if (
      formData.distanceLogicEnabled &&
      (!Number.isFinite(distanceLimitVal) || distanceLimitVal <= 0)
    ) {
      showSnackbar("Please enter a valid distance limit in km", "error");
      return;
    }
    try {
      const serviceData = {
        title: formData.title,
        name: formData.title,
        description: formData.description,
        price: priceVal,
        category: formData.category,
        subcategory: formData.subcategory || "",
        whatsIncluded: normalizeIncludedItems(formData.whatsIncluded),
        imageFit: formData.imageFit || "cover",
        imagePositionX: Number(formData.imagePositionX) || 50,
        imagePositionY: Number(formData.imagePositionY) || 50,
        updatedAt: new Date(),
        duration: formData.duration || "",
        status: formData.status || "active",
        discount: parseFloat(formData.discount) || 0,
        commissionRate:
          parseFloat(formData.commissionRate) ||
          platformSettings?.defaultCommissionRate ||
          20,
        distanceLogicEnabled: Boolean(formData.distanceLogicEnabled),
        distanceLimitKm: formData.distanceLogicEnabled
          ? distanceLimitVal
          : getDistanceLimitKm(formData),
      };
      console.log(`${DISTANCE_LOG_PREFIX} Saving service distance setting`, {
        serviceId: editingService?.id || "(new service)",
        title: serviceData.title,
        previousEnabled: editingService
          ? isDistanceLogicEnabled(editingService)
          : null,
        previousLimitKm: editingService
          ? getDistanceLimitKm(editingService)
          : null,
        distanceLogicEnabled: serviceData.distanceLogicEnabled,
        distanceLimitKm: serviceData.distanceLimitKm,
      });
      // provider fields intentionally omitted — simplified service record

      // If no image file selected, try to use the selected subcategory's image (if any)
      if (!selectedImageFile && formData.subcategory) {
        const sel = subcategories.find((s) => s.name === formData.subcategory);
        if (sel && sel.imageUrl) {
          serviceData.imageUrl = sel.imageUrl;
          if (sel.imagePath) serviceData.imagePath = sel.imagePath;
        }
      }

      // Preserve existing image when editing and no new file selected
      if (editingService && !selectedImageFile && editingService.imageUrl) {
        serviceData.imageUrl = editingService.imageUrl;
      }

      // Upload image if selected
      if (selectedImageFile) {
        try {
          // Ensure user is signed in before attempting upload — Storage rules often require auth
          if (!auth || !auth.currentUser) {
            showSnackbar(
              "You must be signed in to upload images. Please sign in and try again.",
              "error"
            );
            return;
          }

          const imgRef = storageRef(
            storage,
            `service_images/service_${Date.now()}_${selectedImageFile.name}`
          );
          await uploadBytes(imgRef, selectedImageFile);
          const url = await getDownloadURL(imgRef);
          serviceData.imageUrl = url;
        } catch (err) {
          console.error("Error uploading service image:", err);
          // Provide clearer guidance when permissions are the problem
          if (err && err.code === "storage/unauthorized") {
            showSnackbar(
              "Upload failed: you do not have permission to upload to Storage. Check Firebase Storage rules and ensure your account is authorized.",
              "error"
            );
          } else {
            showSnackbar("Failed to upload image. Please try again.", "error");
          }
          return;
        }
      }
      if (editingService) {
        await updateDoc(doc(db, "services", editingService.id), serviceData);
        console.log(`${DISTANCE_LOG_PREFIX} Saved to Firestore`, {
          serviceId: editingService.id,
          distanceLogicEnabled: serviceData.distanceLogicEnabled,
          distanceLimitKm: serviceData.distanceLimitKm,
        });
        setServices((prev) =>
          prev.map((s) =>
            s.id === editingService.id ? { ...s, ...serviceData } : s
          )
        );
        // Show success alert on edit
        showSnackbar("Service updated successfully", "success");
      } else {
        serviceData.createdAt = new Date();
        const docRef = await addDoc(collection(db, "services"), serviceData);
        console.log(`${DISTANCE_LOG_PREFIX} Created service in Firestore`, {
          serviceId: docRef.id,
          distanceLogicEnabled: serviceData.distanceLogicEnabled,
          distanceLimitKm: serviceData.distanceLimitKm,
        });
        setServices((prev) => [{ id: docRef.id, ...serviceData }, ...prev]);
        // Show success alert on add
        showSnackbar("Service added successfully", "success");
      }
      setSelectedImageFile(null);
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving service:", err);
      showSnackbar("Failed to save service", "error");
    }
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await deleteDoc(doc(db, "services", serviceToDelete.id));
      setServices((prev) =>
        prev.filter((service) => service.id !== serviceToDelete.id)
      );
      showSnackbar("Service deleted successfully", "success");
    } catch (err) {
      console.error("Error deleting service:", err);
      showSnackbar("Failed to delete service", "error");
    } finally {
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
    }
  };

  const toggleServiceStatus = async (service) => {
    if (!service || !service.id) return;
    const current = (service.status || "").toString().toLowerCase();
    const next = current === "active" ? "paused" : "active";
    try {
      await updateDoc(doc(db, "services", service.id), {
        status: next,
        updatedAt: new Date(),
      });
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id
            ? { ...s, status: next, updatedAt: new Date() }
            : s
        )
      );
      showSnackbar(
        `Service ${next === "active" ? "activated" : "paused"} successfully`,
        "success"
      );
    } catch (err) {
      console.error("Failed to toggle service status", err);
      showSnackbar("Failed to update service status", "error");
    }
  };

  const toggleServiceDistanceLogic = async (service) => {
    if (!service || !service.id) return;
    const title = service.title || service.name || "Untitled Service";
    const current = isDistanceLogicEnabled(service);
    const next = !current;
    const limitKm = getDistanceLimitKm(service);
    const update = {
      distanceLogicEnabled: next,
      distanceLimitKm: limitKm,
      updatedAt: new Date(),
    };
    console.log(`${DISTANCE_LOG_PREFIX} Toggle requested`, {
      serviceId: service.id,
      title,
      storedValue: service.distanceLogicEnabled,
      from: current ? "ON" : "OFF",
      to: next ? "ON" : "OFF",
      distanceLimitKm: limitKm,
    });
    setDistanceTogglingId(service.id);
    try {
      await updateDoc(doc(db, "services", service.id), update);
      console.log(`${DISTANCE_LOG_PREFIX} Toggle saved to Firestore`, {
        serviceId: service.id,
        title,
        distanceLogicEnabled: next,
        distanceLimitKm: limitKm,
      });
      setServices((prev) =>
        prev.map((s) => (s.id === service.id ? { ...s, ...update } : s))
      );
      showSnackbar(
        next
          ? `${limitKm} km distance limit turned ON for "${title}"`
          : `Distance limit turned OFF for "${title}"`,
        "success"
      );
    } catch (err) {
      console.error(`${DISTANCE_LOG_PREFIX} Toggle failed`, {
        serviceId: service.id,
        title,
        attempted: next ? "ON" : "OFF",
        error: err,
      });
      showSnackbar("Failed to update distance setting", "error");
    } finally {
      setDistanceTogglingId(null);
    }
  };

  const renderDistanceToggle = (service) => {
    const enabled = isDistanceLogicEnabled(service);
    return (
      <FormControlLabel
        sx={{ mr: 0 }}
        control={
          <Switch
            size="small"
            checked={enabled}
            disabled={distanceTogglingId === service.id}
            onChange={() => toggleServiceDistanceLogic(service)}
          />
        }
        label={
          <Typography variant="body2">
            {enabled
              ? `Distance limit: ON (${getDistanceLimitKm(service)} km)`
              : "Distance limit: OFF"}
          </Typography>
        }
      />
    );
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const calculateEarnings = (price, commissionRate, discount) => {
    // Ensure all values are numbers
    const numPrice = parseFloat(price) || 0;
    const numCommissionRate = parseFloat(commissionRate) || 0;
    const numDiscount = parseFloat(discount) || 0;

    const discountedPrice =
      numDiscount > 0 ? numPrice - (numPrice * numDiscount) / 100 : numPrice;
    const commission = (discountedPrice * numCommissionRate) / 100;
    const platformFee = platformSettings?.platformFee || 5;
    const providerEarnings = discountedPrice - commission - platformFee;

    return {
      originalPrice: numPrice,
      discountedPrice,
      commission,
      platformFee,
      providerEarnings,
    };
  };

  const formatDate = (dateField) => {
    if (!dateField) return "N/A";
    if (dateField.toDate && typeof dateField.toDate === "function")
      return dateField.toDate().toLocaleDateString();
    if (dateField instanceof Date) return dateField.toLocaleDateString();
    if (typeof dateField === "string") {
      try {
        return new Date(dateField).toLocaleDateString();
      } catch {
        return dateField;
      }
    }
    // Support RN-style date map objects: { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return "N/A";
  };

  const serviceStatusColor = (status) => {
    const s = (status || "").toString().toLowerCase();
    if (s === "active") return "success";
    if (s === "paused") return "warning";
    if (s === "inactive" || s === "disabled") return "default";
    return "default";
  };

  const filteredServices = services.filter((s) => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return true;
    const cat = (s.category || s.subcategory || s.categoryName || "")
      .toString()
      .toLowerCase();
    if (cat.includes(q)) return true;
    const title = (s.title || s.name || "").toString().toLowerCase();
    return title.includes(q);
  });

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div>
          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300, mr: 2 }}
          />
        </div>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newView) => newView && setViewMode(newView)}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add New Service
          </Button>
        </Box>
      </Box>

      {/* Snackbar placed at top center so it's noticeable (appears below header) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography>Loading services...</Typography>
      ) : filteredServices.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No services found. Click "Add New Service" to create one.
            </Typography>
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <Grid container spacing={2}>
          {filteredServices.map((service) => {
            const earnings = calculateEarnings(
              service.price || 0,
              service.commissionRate ||
                platformSettings?.defaultCommissionRate ||
                20,
              service.discount || 0
            );
            const includedItems = normalizeIncludedItems(
              service.whatsIncluded ||
                service.whatIncluded ||
                service.included ||
                service.includes ||
                service.features
            );
            return (
              <Grid
                item
                size={{ xs: 12, md: 6, lg: 4 }}
                sx={{ display: "flex" }}
                key={service.id}
              >
                <Card
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {service.title || service.name || "Untitled Service"}
                      </Typography>
                      {service.status && (
                        <Chip
                          label={
                            (service.status || "")
                              .toString()
                              .charAt(0)
                              .toUpperCase() +
                            (service.status || "").toString().slice(1)
                          }
                          size="small"
                          color={serviceStatusColor(service.status)}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {service.description || "No about us content"}
                    </Typography>
                    {includedItems.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          What&apos;s Included
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {includedItems.map((item, index) => (
                            <Chip
                              key={`${item}-${index}`}
                              label={item}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {(service.category || service.subcategory) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Category
                        </Typography>
                        <Typography variant="body2">
                          {service.category}
                          {service.category && service.subcategory && " → "}
                          {service.subcategory}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        gutterBottom
                      >
                        Pricing Details
                      </Typography>
                      <Typography variant="body2">
                        Service Price:{" "}
                        <strong>${earnings.originalPrice.toFixed(2)}</strong>
                      </Typography>
                      {service.discount > 0 && (
                        <Typography variant="body2" color="error">
                          Discount: {service.discount}% (-$
                          {(
                            earnings.originalPrice - earnings.discountedPrice
                          ).toFixed(2)}
                          )
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Commission:{" "}
                        {service.commissionRate ||
                          platformSettings?.defaultCommissionRate}
                        % (-${earnings.commission.toFixed(2)})
                      </Typography>
                      <Typography variant="body2">
                        Platform Fee: -${earnings.platformFee.toFixed(2)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ fontWeight: "bold", mt: 0.5 }}
                      >
                        Provider Earnings: $
                        {earnings.providerEarnings.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Created: {formatDate(service.createdAt)}
                    </Typography>
                    {service.ownerName && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Owner: {service.ownerName}
                      </Typography>
                    )}
                  </CardContent>
                  <Box sx={{ px: 2 }}>{renderDistanceToggle(service)}</Box>
                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(service)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        service.status &&
                        service.status.toString().toLowerCase() === "active" ? (
                          <PauseIcon />
                        ) : (
                          <PlayArrowIcon />
                        )
                      }
                      onClick={() => toggleServiceStatus(service)}
                      color={
                        service.status &&
                        service.status.toString().toLowerCase() === "active"
                          ? "warning"
                          : "success"
                      }
                    >
                      {service.status &&
                      service.status.toString().toLowerCase() === "active"
                        ? "Pause"
                        : "Activate"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filteredServices.map((service) => {
            const earnings = calculateEarnings(
              service.price || 0,
              service.commissionRate ||
                platformSettings?.defaultCommissionRate ||
                20,
              service.discount || 0
            );
            const includedItems = normalizeIncludedItems(
              service.whatsIncluded ||
                service.whatIncluded ||
                service.included ||
                service.includes ||
                service.features
            );
            return (
              <Card key={service.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {service.title ||
                              service.name ||
                              "Untitled Service"}
                          </Typography>
                          {service.status && (
                            <Chip
                              label={
                                (service.status || "")
                                  .toString()
                                  .charAt(0)
                                  .toUpperCase() +
                                (service.status || "").toString().slice(1)
                              }
                              size="small"
                              color={serviceStatusColor(service.status)}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {service.description || "No about us content"}
                      </Typography>
                      {includedItems.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            What&apos;s Included
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ mt: 0.75 }}
                          >
                            {includedItems.map((item, index) => (
                              <Chip
                                key={`${item}-${index}`}
                                label={item}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {(service.category || service.subcategory) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Category
                          </Typography>
                          <Typography variant="body2">
                            {service.category}
                            {service.category && service.subcategory && " → "}
                            {service.subcategory}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Service Price:
                          </Typography>
                          <Typography variant="h6" color="primary">
                            ${earnings.originalPrice.toFixed(2)}
                          </Typography>
                        </Box>
                        {service.discount > 0 && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Discount:
                            </Typography>
                            <Typography variant="body1" color="error">
                              {service.discount}%
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Commission:
                          </Typography>
                          <Typography variant="body1">
                            {service.commissionRate ||
                              platformSettings?.defaultCommissionRate}
                            %
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Provider Earnings:
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            ${earnings.providerEarnings.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Created: {formatDate(service.createdAt)}
                        {service.ownerName && ` | Owner: ${service.ownerName}`}
                      </Typography>
                      <Box sx={{ mt: 1 }}>{renderDistanceToggle(service)}</Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(service)}
                        variant="outlined"
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={
                          service.status &&
                          service.status.toString().toLowerCase() ===
                            "active" ? (
                            <PauseIcon />
                          ) : (
                            <PlayArrowIcon />
                          )
                        }
                        onClick={() => toggleServiceStatus(service)}
                        variant="outlined"
                        color={
                          service.status &&
                          service.status.toString().toLowerCase() === "active"
                            ? "warning"
                            : "success"
                        }
                      >
                        {service.status &&
                        service.status.toString().toLowerCase() === "active"
                          ? "Pause"
                          : "Activate"}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button onClick={() => setDialogOpen(false)} size="small">
            Back to Services
          </Button>
          <Typography variant="h6">
            {editingService ? "Edit Service" : "Add New Service"}
          </Typography>
          <Box />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 3 }}>
                <Stack alignItems="center" spacing={1} sx={{ pt: 1 }}>
                  <Box
                    sx={{
                      width: 160,
                      height: 120,
                      borderRadius: 2,
                      overflow: "hidden",
                      backgroundColor: "#eee",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    {imagePreview || (editingService && editingService.imageUrl) ? (
                      <Box
                        component="img"
                        src={
                          imagePreview ||
                          (editingService && editingService.imageUrl) ||
                          ""
                        }
                        alt={formData.title || "Service image"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: formData.imageFit || "cover",
                          objectPosition: getImagePosition(
                            formData.imagePositionX,
                            formData.imagePositionY
                          ),
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: "100%",
                          height: "100%",
                          backgroundColor: "#eee",
                          borderRadius: 0,
                        }}
                        alt={formData.title || "Service image"}
                      />
                    )}
                  </Box>
                  <input
                    accept="image/*"
                    id="service-image-input"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                  <label htmlFor="service-image-input">
                    <IconButton
                      color="primary"
                      aria-label="upload picture"
                      component="span"
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  </label>
                  <Typography variant="caption" color="text.secondary">
                    {selectedImageFile
                      ? selectedImageFile.name
                      : imagePreview ||
                        (editingService && editingService.imageUrl)
                      ? "Using selected image"
                      : "No file chosen"}
                  </Typography>
                  {(imagePreview || (editingService && editingService.imageUrl)) && (
                    <Stack spacing={1} sx={{ width: "100%", mt: 1 }}>
                      <TextField
                        select
                        size="small"
                        label="Image Fit"
                        name="imageFit"
                        value={formData.imageFit || "cover"}
                        onChange={handleInputChange}
                        fullWidth
                      >
                        <MenuItem value="cover">Cover area</MenuItem>
                        <MenuItem value="contain">Fit full image</MenuItem>
                      </TextField>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Horizontal position
                        </Typography>
                        <Slider
                          size="small"
                          value={Number(formData.imagePositionX) || 50}
                          min={0}
                          max={100}
                          onChange={(event, value) =>
                            setFormData((prev) => ({
                              ...prev,
                              imagePositionX: value,
                            }))
                          }
                          disabled={(formData.imageFit || "cover") === "contain"}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Vertical position
                        </Typography>
                        <Slider
                          size="small"
                          value={Number(formData.imagePositionY) || 50}
                          min={0}
                          max={100}
                          onChange={(event, value) =>
                            setFormData((prev) => ({
                              ...prev,
                              imagePositionY: value,
                            }))
                          }
                          disabled={(formData.imageFit || "cover") === "contain"}
                        />
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            imageFit: "cover",
                            imagePositionX: 50,
                            imagePositionY: 50,
                          }))
                        }
                      >
                        Reset Image Position
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, md: 9 }}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Service Name"
                      placeholder="Enter service name"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>

                  {/* Provider assignment and alternative name removed — simplified service form */}

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="category-label">Category</InputLabel>
                      <Select
                        labelId="category-label"
                        label="Category"
                        name="category"
                        value={formData.category}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            category: value,
                            subcategory: "",
                          }));
                          const sel = categories.find((c) => c.name === value);
                          if (sel) {
                            fetchSubcategories(sel.id);
                          } else {
                            setSubcategories([]);
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>-- Select category --</em>
                        </MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.id} value={c.name}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="subcategory-label">
                        Sub Category
                      </InputLabel>
                      <Select
                        labelId="subcategory-label"
                        label="Sub Category"
                        name="subcategory"
                        value={formData.subcategory}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            subcategory: value,
                          }));
                          const sel = subcategories.find(
                            (s) => s.name === value
                          );
                          if (sel && sel.imageUrl && !selectedImageFile) {
                            try {
                              setImagePreview(sel.imageUrl);
                            } catch {}
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a subcategory</em>
                        </MenuItem>
                        {subcategories.map((s) => (
                          <MenuItem key={s.id} value={s.name}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Rate ($)"
                      placeholder="Enter rate"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Duration (minutes)"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      fullWidth
                      helperText="Enter duration in minutes"
                    />
                  </Grid>

                  <Grid
                    item
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(formData.status || "active") === "active"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                        />
                      }
                      label={
                        (formData.status || "active") === "active"
                          ? "Active"
                          : "Inactive"
                      }
                    />
                  </Grid>

                  <Grid
                    item
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formData.distanceLogicEnabled)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            console.log(
                              `${DISTANCE_LOG_PREFIX} Form switch changed`,
                              {
                                serviceId: editingService?.id || "(new service)",
                                to: checked ? "ON" : "OFF",
                              }
                            );
                            setFormData((prev) => ({
                              ...prev,
                              distanceLogicEnabled: checked,
                            }));
                          }}
                        />
                      }
                      label={
                        formData.distanceLogicEnabled
                          ? "Distance limit: ON"
                          : "Distance limit: OFF (no distance restriction)"
                      }
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Distance limit"
                      name="distanceLimitKm"
                      type="number"
                      value={formData.distanceLimitKm}
                      onChange={handleInputChange}
                      disabled={!formData.distanceLogicEnabled}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">km</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 1, step: 1 }}
                      helperText="Used only when the distance limit is ON"
                    />
                  </Grid>

                  {/* Discount and Commission Fields */}
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Discount"
                      name="discount"
                      type="number"
                      value={formData.discount}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        const maxDiscount = platformSettings?.enableDiscounts
                          ? platformSettings?.maxDiscountPercentage || 50
                          : 0;
                        if (!platformSettings?.enableDiscounts) {
                          showSnackbar(
                            "Discounts are currently disabled in Platform Settings",
                            "warning"
                          );
                          return;
                        }
                        if (value > maxDiscount) {
                          showSnackbar(
                            `Maximum discount is ${maxDiscount}%`,
                            "warning"
                          );
                          setFormData((prev) => ({
                            ...prev,
                            discount: maxDiscount,
                          }));
                        } else {
                          setFormData((prev) => ({ ...prev, discount: value }));
                        }
                      }}
                      fullWidth
                      disabled={!platformSettings?.enableDiscounts}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      helperText={
                        !platformSettings?.enableDiscounts
                          ? "Discounts disabled in settings"
                          : `Max: ${
                              platformSettings?.maxDiscountPercentage || 50
                            }%`
                      }
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Commission Rate"
                      name="commissionRate"
                      type="number"
                      value={formData.commissionRate}
                      onChange={handleInputChange}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      helperText={`Default: ${
                        platformSettings?.defaultCommissionRate || 20
                      }%`}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 12 }}>
                    <TextField
                      size="small"
                      label="About Us"
                      placeholder="Tell customers about this service..."
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 12 }}>
                    <TextField
                      size="small"
                      label="What's Included"
                      placeholder="Enter each included item on a new line"
                      name="whatsIncluded"
                      value={formData.whatsIncluded}
                      onChange={handleInputChange}
                      multiline
                      rows={4}
                      fullWidth
                      helperText="Example: Product consultation, equipment, cleanup"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Earnings Preview */}
            {formData.price && parseFloat(formData.price) > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Earnings Preview
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Service Price:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ${parseFloat(formData.price || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  {formData.discount > 0 && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        After Discount:
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                      >
                        $
                        {(
                          parseFloat(formData.price || 0) *
                          (1 - parseFloat(formData.discount || 0) / 100)
                        ).toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Commission:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error">
                      -$
                      {(
                        (parseFloat(formData.price || 0) *
                          (1 - parseFloat(formData.discount || 0) / 100) *
                          parseFloat(formData.commissionRate || 0)) /
                        100
                      ).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Provider Earns:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="success.main"
                    >
                      $
                      {(
                        parseFloat(formData.price || 0) *
                          (1 - parseFloat(formData.discount || 0) / 100) -
                        (parseFloat(formData.price || 0) *
                          (1 - parseFloat(formData.discount || 0) / 100) *
                          parseFloat(formData.commissionRate || 0)) /
                          100 -
                        (platformSettings?.platformFee || 0)
                      ).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  * Platform fee (${platformSettings?.platformFee || 0}) is
                  charged once per order, not per service
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingService ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete Service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the service "
            {serviceToDelete?.title || serviceToDelete?.name}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* snackbar is rendered above near the header */}
    </Box>
  );
};

export default Services;
